import React, { FC, memo } from 'react';

import classNames from 'classnames';
import IconChevronDown from '@zvuk-b2b/react-uikit/ui/icons/ChevronDown';

import PromoListSkeleton from '../../ReportRequestForm/PromoSelector/PromoListSkeleton';
import PromoSelectorSearchRow from '../../ReportRequestForm/PromoSelector/PromoSelectorSearchRow';

import DailyPromoOption from './DailyPromoOption';
import { DATA_TEST_IDS } from '../consts';
import { TEXTS } from '../texts';
import type { DailyPromoSelectProps } from './types';
import { useDailyPromoSelectViewModel } from './useDailyPromoSelectViewModel';

import styles from './index.module.scss';

const DailyPromoSelect: FC<DailyPromoSelectProps> = ({ mediafileId, onChange }) => {
  const {
    containerRef,
    dropdownState,
    fileUuid,
    filterButtonRef,
    filterContainerRef,
    isDropdownOpen,
    isFilterOpen,
    isInitialLoading,
    isLoading,
    isNextPageLoading,
    isPaused,
    items,
    queryError,
    search,
    selectedPromo,
    selectedStatuses,
    sort,
    statusesInfo,
    triggerRef,
    onDropdownToggle,
    onFilterToggle,
    onListScroll,
    onPromoPlaybackToggle,
    onRetry,
    onSearchChange,
    onSelectPromo,
    onSortToggle,
    onStatusChange,
  } = useDailyPromoSelectViewModel({ mediafileId, onChange });

  return (
    <div
      className={styles.container}
      data-testid={DATA_TEST_IDS.PROMO_SELECT}
      ref={containerRef}>
      <span className={styles.label}>{TEXTS.PROMO_LABEL}</span>
      <button
        ref={triggerRef}
        type="button"
        className={classNames(styles.trigger, { [styles.triggerOpen]: isDropdownOpen })}
        data-testid={DATA_TEST_IDS.PROMO_TRIGGER}
        aria-controls={DATA_TEST_IDS.PROMO_DROPDOWN}
        aria-expanded={isDropdownOpen}
        onClick={onDropdownToggle}>
        <span className={classNames(styles.triggerTitle, { [styles.placeholder]: !selectedPromo })}>
          {selectedPromo?.title || TEXTS.PROMO_PLACEHOLDER}
        </span>
        <span
          className={classNames(styles.triggerIcon, { [styles.triggerIconOpen]: isDropdownOpen })}
          data-testid={DATA_TEST_IDS.PROMO_TRIGGER_ICON}>
          <IconChevronDown aria-hidden="true" />
        </span>
      </button>
      {isDropdownOpen && (
        <div
          id={DATA_TEST_IDS.PROMO_DROPDOWN}
          className={styles.dropdown}
          data-testid={DATA_TEST_IDS.PROMO_DROPDOWN}
          style={{
            position: 'fixed',
            maxHeight: dropdownState.maxHeight,
            top: dropdownState.fixedPosition?.top,
            left: dropdownState.fixedPosition?.left,
            width: dropdownState.fixedPosition?.width,
          }}>
          <PromoSelectorSearchRow
            filterButtonRef={filterButtonRef}
            filterContainerRef={filterContainerRef}
            isFilterOpen={isFilterOpen}
            search={search}
            selectedStatuses={selectedStatuses}
            sort={sort}
            statusesInfo={statusesInfo}
            onFilterToggle={onFilterToggle}
            onSearchChange={onSearchChange}
            onSortToggle={onSortToggle}
            onStatusChange={onStatusChange}
          />
          <div className={styles.divider} />
          <div
            className={styles.list}
            data-testid={DATA_TEST_IDS.PROMO_LIST}
            aria-busy={isInitialLoading || isNextPageLoading}
            onScroll={onListScroll}>
            {isInitialLoading && <PromoListSkeleton />}
            {!isInitialLoading && queryError && (
              <div
                className={styles.state}
                data-testid={DATA_TEST_IDS.PROMO_ERROR}>
                <span>{TEXTS.PROMO_ERROR}</span>
                <button
                  type="button"
                  data-testid={DATA_TEST_IDS.PROMO_RETRY}
                  onClick={onRetry}>
                  {TEXTS.PROMO_RETRY}
                </button>
              </div>
            )}
            {!isInitialLoading && !queryError && items.length === 0 && (
              <div
                className={styles.state}
                data-testid={DATA_TEST_IDS.PROMO_EMPTY}>
                {TEXTS.PROMO_EMPTY}
              </div>
            )}
            {!isInitialLoading &&
              !queryError &&
              items.map((promo) => (
                <DailyPromoOption
                  key={promo.id}
                  isCurrentPromo={fileUuid === promo.id}
                  isLoading={isLoading}
                  isPaused={isPaused}
                  isSelected={promo.mediafileId === mediafileId}
                  promo={promo}
                  onPlaybackToggle={onPromoPlaybackToggle}
                  onSelect={onSelectPromo}
                />
              ))}
            {isNextPageLoading && <PromoListSkeleton rowsCount={1} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(DailyPromoSelect);
